import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

export type AvatarTamanho = 'sm' | 'md' | 'lg' | 'xl';
/** `solida`: fundo verde sólido (padrão). `suave`: verde translúcido com texto verde claro (usuários). */
export type AvatarVariante = 'solida' | 'suave';

const CLASSES_TAMANHO: Record<AvatarTamanho, string> = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-12 text-base',
  xl: 'size-14 text-base',
};

const CLASSES_BASE =
  'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold';

const CLASSES_VARIANTE: Record<AvatarVariante, string> = {
  solida: 'bg-rfy-primary text-rfy-primary-contrast',
  suave: 'bg-rfy-primary/25 text-rfy-link-hover',
};

const primeiraLetra = (palavra: string | undefined): string => (Array.from(palavra ?? '')[0] ?? '').toUpperCase();

/** Iniciais do primeiro e do último nome (`'Ana Maria Souza'` → `'AS'`; vazio → `'?'`). */
export const obterIniciais = (nome: string | null | undefined): string => {
  const palavras = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return '?';
  const primeira = primeiraLetra(palavras[0]);
  return palavras.length === 1 ? primeira : primeira + primeiraLetra(palavras[palavras.length - 1]);
};

/**
 * Avatar circular com a foto ou, sem foto (ou se ela falhar), as iniciais do nome.
 * Decorativo por padrão (`aria-hidden`); passe `rotulo` para expor como imagem.
 * Use `variante="suave"` para avatar de usuário (fundo verde translúcido) e `tamanho="xl"` para logo/destaque.
 */
@Component({
  selector: 'app-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
    '[attr.aria-hidden]': 'rotulo() ? null : "true"',
    '[attr.role]': 'rotulo() ? "img" : null',
    '[attr.aria-label]': 'rotulo() || null',
    '[attr.title]': 'nome() || null',
  },
  template: `
    @if (foto() && !fotoFalhou()) {
      <img [src]="foto()" alt="" class="size-full object-cover" (error)="fotoFalhou.set(true)" />
    } @else {
      {{ iniciais() }}
    }
  `,
})
export class AvatarComponent {
  readonly nome = input.required<string>();
  readonly foto = input<string | null>();
  readonly variante = input<AvatarVariante>('solida');
  readonly tamanho = input<AvatarTamanho>('md');
  readonly rotulo = input<string>();

  protected readonly fotoFalhou = signal(false);
  protected readonly iniciais = computed(() => obterIniciais(this.nome()));
  protected readonly classes = computed(
    () => `${CLASSES_BASE} ${CLASSES_VARIANTE[this.variante()]} ${CLASSES_TAMANHO[this.tamanho()]}`,
  );
}
